import { withAuthRecovery } from '@/lib/authRecovery'
import { errorMessage } from '@/lib/errorMessage'
import { getSupabase } from '@/lib/supabase'

/**
 * Доступ в управляющую компанию: заявки и приглашения.
 *
 * Решение владельца 30.08.2026: выдаёт доступ только супер-админ, остальные
 * сотрудники УК оставляют заявку. Гейты здесь не дублируются — их держит сервер
 * (`public.is_super_admin()` внутри каждой пишущей RPC); экран лишь прячет то,
 * чего всё равно не отдадут.
 */

type RpcResult<T> = { data: T | null; error: unknown }
type RpcClient = {
  rpc: <T>(functionName: string, args?: Record<string, unknown>) => PromiseLike<RpcResult<T>>
}

function rpcClient(): RpcClient {
  return getSupabase() as unknown as RpcClient
}

async function call<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await withAuthRecovery(() => rpcClient().rpc<T>(fn, args))
  if (error) throw new Error(errorMessage(error, `Не удалось выполнить операцию`))
  return (data ?? []) as T
}

export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
export type ManagementAccessLevel = 'standard' | 'advanced'

/** Заявка глазами супер-админа: с автором, исходом и выданным инвайтом. */
export type ManagementAccessRequest = {
  id: number
  requested_by_user_id: string
  requested_by_name: string | null
  target_email: string
  target_first_name: string
  target_last_name: string
  target_position: string
  requested_access_level: ManagementAccessLevel
  reason: string | null
  status: AccessRequestStatus
  created_at: string
  resolved_at: string | null
  resolved_by_name: string | null
  resolution_note: string | null
  invite_id: number | null
  invite_code: string | null
  invite_is_active: boolean | null
  invite_valid_until: string | null
  invite_current_uses: number | null
}

/** Своя заявка глазами автора: без служебных полей чужого решения. */
export type MyAccessRequest = {
  id: number
  target_email: string
  target_first_name: string
  target_last_name: string
  target_position: string
  requested_access_level: ManagementAccessLevel
  reason: string | null
  status: AccessRequestStatus
  created_at: string
  resolved_at: string | null
  resolution_note: string | null
}

export type ManagementInvite = {
  id: number
  invite_code: string
  access_level: ManagementAccessLevel
  max_uses: number
  current_uses: number
  valid_until: string | null
  is_active: boolean
  created_at: string
  description: string | null
  target_email: string | null
  target_first_name: string | null
  target_last_name: string | null
  target_position: string | null
  is_expired: boolean
  is_exhausted: boolean
}

export type NewAccessRequest = {
  email: string
  firstName: string
  lastName: string
  position: string
  reason?: string | null
  accessLevel: ManagementAccessLevel
}

export async function createAccessRequest(input: NewAccessRequest) {
  return call<{ request_id: number; status: AccessRequestStatus; created_at: string }[]>(
    'create_management_access_request',
    {
      p_target_email: input.email.trim().toLowerCase(),
      p_target_first_name: input.firstName.trim(),
      p_target_last_name: input.lastName.trim(),
      p_target_position: input.position.trim(),
      p_reason: input.reason?.trim() || null,
      p_requested_access_level: input.accessLevel,
    }
  )
}

export async function fetchMyAccessRequests(limit = 50): Promise<MyAccessRequest[]> {
  return call<MyAccessRequest[]>('list_my_management_access_requests', { p_limit: limit })
}

export async function cancelAccessRequest(requestId: number, note?: string) {
  return call('cancel_management_access_request', {
    p_request_id: requestId,
    p_note: note?.trim() || null,
  })
}

export async function fetchAccessRequests(
  status?: AccessRequestStatus | null
): Promise<ManagementAccessRequest[]> {
  return call<ManagementAccessRequest[]>('list_management_access_requests', {
    p_status: status ?? null,
    p_limit: 200,
  })
}

export async function approveAccessRequest(requestId: number, validDays = 7, note?: string) {
  const rows = await call<
    { request_id: number; invite_id: number; invite_code: string; valid_until: string }[]
  >('approve_management_access_request', {
    p_request_id: requestId,
    p_valid_days: validDays,
    p_note: note?.trim() || null,
  })
  const result = rows[0]
  if (!result) throw new Error('Сервер не вернул созданное приглашение')
  return result
}

export async function rejectAccessRequest(requestId: number, note: string) {
  return call('reject_management_access_request', {
    p_request_id: requestId,
    p_note: note.trim(),
  })
}

/**
 * ⚠️ RPC отдаёт ВСЕ инвайты сети, а не только созданные вызывающим, и вместе с
 * кодами. Поэтому она сужена до супер-админа той же миграцией, что и выдача:
 * код непривязанного инвайта сам по себе даёт вход в УК.
 */
export async function fetchManagementInvites(): Promise<ManagementInvite[]> {
  return call<ManagementInvite[]>('get_management_company_invites_v2', { p_user_id: null })
}

export async function deactivateInvite(inviteId: number) {
  const rows = await call<{ success: boolean; message: string }[]>(
    'deactivate_management_company_invite_v2',
    { p_invite_id: inviteId, p_user_id: null }
  )
  const result = rows[0]
  if (!result) throw new Error('Сервер не вернул результат деактивации приглашения')
  if (result && result.success === false) throw new Error(result.message)
  return result
}

/**
 * Операции над действующим сотрудником УК. Все три возвращают
 * `(success, message)` вместо исключения, поэтому отказ приходится
 * разворачивать в ошибку здесь — иначе экран покажет «готово» на отказе.
 */
async function memberAction(fn: string, targetUserId: string) {
  const rows = await call<{ success: boolean; message: string }[]>(fn, {
    p_target_user_id: targetUserId,
    p_target_telegram_id: null,
    p_user_id: null,
  })
  const result = rows[0]
  if (!result || result.success !== true) {
    throw new Error(result?.message || 'Операция не выполнена')
  }
  return result.message
}

export const promoteToAdvanced = (userId: string) =>
  memberAction('promote_management_company_user_v2', userId)

export const demoteToStandard = (userId: string) =>
  memberAction('demote_management_company_user_v2', userId)

export const deactivateManagementAccess = (userId: string) =>
  memberAction('deactivate_management_company_user_v2', userId)

export const activateManagementAccess = (userId: string) =>
  memberAction('activate_management_company_user_v2', userId)
